import { useMemo, useState } from "react";
import { api, ApiError } from "../api/client";
import { useQuery } from "../api/useQuery";
import { fmtDateTime } from "../components/format";
import type { UnknownTag, User } from "../api/types";

type TokenStatus = "active" | "blocked" | "lost" | "replaced";

const STATUS_NAME: Record<TokenStatus, string> = {
  active: "Действует",
  blocked: "Заблокирован",
  lost: "Утрачен",
  replaced: "Заменён",
};

interface Token {
  id: string;
  uid_hex: string;
  serial_no: string;
  user_id: string;
  user_name: string;
  territory_name: string | null;
  status: TokenStatus;
  note: string;
  issued_at: string;
  can_edit: boolean;
}

/**
 * Личные NFC-жетоны инспекторов. Жетон открывает приложение вместо PIN-кода
 * на уже активированном телефоне; активация и смена устройства — только по паролю.
 */
export function TokensPage() {
  const items = useQuery<{ items: Token[] }>("/api/v1/tokens");
  const users = useQuery<{ items: User[] }>("/api/v1/users");
  const unknown = useQuery<{ items: UnknownTag[] }>("/api/v1/tags/unknown");
  const [selected, setSelected] = useState<string | null>(null);
  const [issuing, setIssuing] = useState<string | null>(null);

  const current = items.data?.items.find((t) => t.id === selected) ?? null;
  const inspectors = useMemo(
    () => (users.data?.items ?? []).filter((u) => u.role === "inspector" && u.is_active && u.can_edit),
    [users.data]);
  const active = (items.data?.items ?? []).filter((t) => t.status === "active").length;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Личные жетоны</h2>
          {items.data && <p className="muted small">Действующих: {active} · всего выдано: {items.data.items.length}</p>}
        </div>
        <button onClick={() => { setIssuing(""); setSelected(null); }} disabled={inspectors.length === 0}>Выдать жетон</button>
      </div>

      {items.error && <div className="error" role="alert">{items.error}</div>}

      <div className="split">
        <div className="split-list">
          <div className="list">
            {items.data && items.data.items.length === 0 && (
              <div className="empty">Жетоны не выданы. Вход по PIN-коду работает и без них.</div>
            )}
            {items.data?.items.map((t) => (
              <button key={t.id} className={`item${t.id === selected && issuing === null ? " selected" : ""}`}
                onClick={() => { setSelected(t.id); setIssuing(null); }}>
                <span className="item-body">
                  <span className="item-head">
                    <span className="item-title">{t.user_name}</span>
                    <span className={`badge${t.status === "active" ? " ok" : t.status === "replaced" ? "" : " danger"}`}>
                      {STATUS_NAME[t.status]}
                    </span>
                  </span>
                  <span className="item-text">
                    <span className="mono">{t.uid_hex}</span>
                    {t.serial_no && ` · № ${t.serial_no}`}
                    {t.territory_name && ` · ${t.territory_name}`}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="split-detail">
          {issuing !== null && (
            <IssueForm key={issuing} userId={issuing} inspectors={inspectors} unknown={unknown.data?.items ?? []}
              onDone={(id) => { items.reload(); unknown.reload(); setIssuing(null); setSelected(id); }}
              onCancel={() => setIssuing(null)} />
          )}
          {issuing === null && current && (
            <section className="card">
              <div className="muted small">Выдан {fmtDateTime(current.issued_at)}</div>
              <h3 style={{ fontSize: 22, margin: "2px 0 12px" }}>{current.user_name}</h3>
              <div className="list" style={{ background: "var(--bg)", marginBottom: 16 }}>
                <div className="row"><span className="muted">UID</span><span className="mono">{current.uid_hex}</span></div>
              </div>
              {current.can_edit ? (
                <>
                  <TokenForm key={current.id + current.status} token={current} onDone={() => items.reload()} />
                  {current.status !== "replaced" && (
                    <p className="muted small" style={{ marginTop: 16 }}>
                      Утерян или сломан?{" "}
                      <button className="secondary" onClick={() => setIssuing(current.user_id)}>Выдать новый жетон</button>
                      {" "}— этот станет заменённым.
                    </p>
                  )}
                </>
              ) : (
                <p className="muted small">Изменять жетоны этой территории вы не можете.</p>
              )}
            </section>
          )}
          {issuing === null && !current && <div className="card empty">Выберите жетон или выдайте новый</div>}
        </div>
      </div>
    </>
  );
}

function IssueForm({ userId, inspectors, unknown, onDone, onCancel }: {
  userId: string;
  inspectors: User[];
  unknown: UnknownTag[];
  onDone: (id: string) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({ user_id: userId || inspectors[0]?.id || "", uid_hex: "", serial_no: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ id: string }>("/api/v1/tokens", form);
      onDone(res.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось выдать жетон");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h3>Выдача жетона</h3>
      <form onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {error && <div className="error" role="alert">{error}</div>}
        <label className="field">Инспектор
          <select value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
            {inspectors.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
          </select>
        </label>
        <label className="field">UID жетона
          <input required value={form.uid_hex} placeholder="04:A1:B2:C3:D4:E5:80" autoComplete="off"
            onChange={(e) => setForm({ ...form, uid_hex: e.target.value })} />
        </label>
        <p className="muted small" style={{ margin: 0 }}>
          UID покажет телефон: приложите жетон к телефону инспектора на экране входа.
        </p>
        {unknown.length > 0 && !form.uid_hex && (
          <div>
            <div className="section-title">Считаны телефоном, но не в реестре</div>
            <div className="list" style={{ background: "var(--bg)" }}>
              {unknown.slice(0, 5).map((u) => (
                <div className="row" key={u.uid_hex}>
                  <span>
                    <span className="mono">{u.uid_hex}</span>
                    <span className="row-sub">{u.inspector} · {fmtDateTime(u.last_read_at)}</span>
                  </span>
                  <button type="button" className="secondary" onClick={() => setForm({ ...form, uid_hex: u.uid_hex })}>Выбрать</button>
                </div>
              ))}
            </div>
          </div>
        )}
        <label className="field">Маркировка на жетоне
          <input value={form.serial_no} placeholder="Например: Ж-014" onChange={(e) => setForm({ ...form, serial_no: e.target.value })} />
        </label>
        <label className="field">Примечание
          <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
        </label>
        <div className="filters" style={{ margin: 0 }}>
          <button type="submit" disabled={busy || !form.user_id}>Выдать</button>
          <button type="button" className="secondary" onClick={onCancel}>Отмена</button>
        </div>
      </form>
    </section>
  );
}

function TokenForm({ token, onDone }: { token: Token; onDone: () => void }) {
  const initial = { status: token.status, serial_no: token.serial_no, note: token.note };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = JSON.stringify(form) !== JSON.stringify(initial);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.put(`/api/v1/tokens/${token.id}`, form);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {error && <div className="error" role="alert">{error}</div>}
      <label className="field">Статус
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as TokenStatus })}>
          {(Object.keys(STATUS_NAME) as TokenStatus[]).map((s) => <option key={s} value={s}>{STATUS_NAME[s]}</option>)}
        </select>
      </label>
      {form.status !== "active" && token.status === "active" && (
        <p className="muted small" style={{ margin: 0 }}>
          Телефон перестанет принимать жетон после ближайшей синхронизации. PIN-код продолжит работать.
        </p>
      )}
      <label className="field">Маркировка на жетоне
        <input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} />
      </label>
      <label className="field">Примечание
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </label>
      <div className="filters" style={{ margin: 0 }}>
        <button type="submit" disabled={busy || !changed}>Сохранить</button>
      </div>
    </form>
  );
}
